import React, { memo, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import {
    Box,
    Typography,
    Chip,
    Card,
    CardContent,
} from "@mui/material";
import { ArrowRightAlt } from "@mui/icons-material";
import { formatTZDate } from 'state/helpers';
import { hasPermission } from "state/checkPermission";
import FlexBetween from "components/FlexBetween";


const DELAY_THRESHOLDS = [
    { minutes: 60, color: 'red.main' },
    { minutes: 30, color: 'orange.main' },
    { minutes: 15, color: 'yellow.main' },
];

const BLINK_STYLES = {
    animation: 'blink 1s step-start 0s infinite',
    fontWeight: 'bold',
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
};

const KEYFRAMES_SX = { '@keyframes blink': { '60%': { opacity: 0.6 } } };


const FlightCard = ({
    index,
    flight,
    onClick,
}) => {
    const timeMode = useSelector((state) => state.timemode);
    const user = useSelector((state) => state.user);
    const canView = useMemo(() =>
        hasPermission(user?.permissions, "FlightSchedule", "view", false),
    [user?.permissions]);

    const flightDate = useMemo(() => {
        return timeMode === "UTC"
            ? formatTZDate(flight?.flightActualDayDep || flight?.flightDate)
            : formatTZDate(flight?.flightActualDayDepLocalBase || flight?.flightDateLocalBase);
    }, [flight, timeMode]);

    const td = useMemo(() =>
        timeMode === "UTC"
            ? flight?.atd || flight?.etd
            : flight?.atdLocalBase || flight?.etdLocalBase,
        [flight, timeMode]
    );

    const ta = useMemo(() =>
        timeMode === "UTC"
            ? flight?.ata || flight?.eta
            : flight?.ataLocalBase || flight?.etaLocalBase,
        [flight, timeMode]
    );

    const getDelayStyles = useCallback((delay, flightDateStr) => {
        if (!delay || isNaN(delay) || delay <= 0) return {};

        const today = timeMode === 'UTC'
            ? new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate()))
            : (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })();

        const isTodayOrFuture = new Date(flightDateStr) >= today;

        const threshold = DELAY_THRESHOLDS.find(t => delay > t.minutes);
        if (!threshold) return {};

        return {
            color: threshold.color,
            ...(isTodayOrFuture && BLINK_STYLES),
        };
    }, [timeMode]);

    const tdStyles = useMemo(() => {
        const flightDateStr = timeMode === 'UTC' ? flight?.flightDate : flight?.flightDateLocalBase;
        const hasAtd = timeMode === 'UTC' ? !!flight?.atd : !!flight?.atdLocalBase;

        if (hasAtd) return getDelayStyles(flight?.depDelaysTime, flightDateStr);

        const fullEtd = flight?.fullEtd;
        if (!fullEtd) return {};
        return getDelayStyles((new Date() - new Date(fullEtd)) / 60000, flightDateStr);
    }, [flight, timeMode, getDelayStyles]);

    const taStyles = useMemo(() => {
        const flightDateStr = timeMode === 'UTC' ? flight?.flightDate : flight?.flightDateLocalBase;
        const hasAta = timeMode === 'UTC' ? !!flight?.ata : !!flight?.ataLocalBase;

        if (hasAta) return getDelayStyles(flight?.arrDelaysTime, flightDateStr);

        const fullEta = flight?.fullEta;
        if (!fullEta) return {};
        return getDelayStyles((new Date() - new Date(fullEta)) / 60000, flightDateStr);
    }, [flight, timeMode, getDelayStyles]);

    const handleClick = useCallback(() => {
        if (canView) {
            onClick(flight);
        }
    }, [canView, onClick, flight]);


    return (
        <Card
            variant="outlined"
            sx={{
                cursor: "pointer",
                bgcolor: "background.default",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "neutral.deep",
                "&:active": {
                    transform: "scale(0.98)",
                    boxShadow: 1,
                },
            }}
            onClick={handleClick}
        >
            <CardContent sx={{ pt: 1.5, "&:last-child": { pb: 1.5 } }}>
                <FlexBetween>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                            label={index + 1}
                            variant="outlined"
                            color="primary"
                            size="small"
                            sx={{
                                fontSize: "0.75rem",
                                borderWidth: 1.5,
                                height: 20,
                                "& .MuiChip-label": { px: 1 },
                            }}
                        />
                        <Typography variant="body1" color="primary.dark">
                            {flight.flightDesc}
                        </Typography>
                    </Box>

                    <Typography variant="body1" color="text.secondary">
                        {flightDate}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {flight.flightReg} | {flight.acType}
                    </Typography>
                </FlexBetween>

                <FlexBetween sx={KEYFRAMES_SX}>
                    <Typography variant="body1" sx={tdStyles}>{td}</Typography>
                    <Typography variant="body1" display="flex" alignItems="center">
                        {flight.dep} <ArrowRightAlt /> {flight.arr}
                    </Typography>
                    <Typography variant="body1" sx={taStyles}>{ta}</Typography>
                </FlexBetween>
            </CardContent>
        </Card>
    );
};

export default memo(FlightCard, (prev, next) => {
    return (
        prev.index === next.index &&
        prev.timeMode === next.timeMode &&
        prev.flight === next.flight
    );
});
